"use client";

import Logo from "@/assets/logo.svg";
import { useGlobalData } from "@/components/TinaContext";
import { Button } from "@/components/ui/button";
import { buttonKey } from "@/lib/list-keys";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "motion/react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";

export default function Navbar() {
  const { navigation } = useGlobalData();
  const { navLinks, navButtons } = navigation || {};
  const pathname = usePathname();

  const { scrollY } = useScroll();
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const scrollPosition = useRef(0);
  const justClosedMenu = useRef(false);

  useMotionValueEvent(scrollY, "change", () => {
    if (justClosedMenu.current) return;

    if (scrollY.current < scrollY.prev) {
      setHidden(false);
    } else if (scrollY.current > 50 && scrollY.current > scrollY.prev) {
      setHidden(true);
    }

    if (!mobileOpen) {
      setScrolled(scrollY.current > 100);
    }
  });

  useEffect(() => {
    if (mobileOpen) {
      setScrolled(true);
      scrollPosition.current = window.scrollY;
      document.body.classList.add("prevent-scroll");
      document.body.style.top = `-${scrollPosition.current}px`;
    } else {
      const wasOpen = scrollPosition.current > 0;

      if (wasOpen) {
        justClosedMenu.current = true;
        setHidden(false);
        document.body.classList.remove("prevent-scroll");
        document.body.style.top = "";
        window.scrollTo(0, scrollPosition.current);

        setTimeout(() => {
          justClosedMenu.current = false;
        }, 500);
      }
    }

    return () => {
      document.body.classList.remove("prevent-scroll");
      document.body.style.top = "";
    };
  }, [mobileOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <motion.nav
      className={cn("sticky top-0 z-50 border-b transition-colors duration-300 bg-background", scrolled ? "border-border" : "border-transparent")}
      animate={hidden ? "hidden" : "visible"}
      variants={{ hidden: { y: "-100%" }, visible: { y: 0 } }}
      transition={{ ease: "easeInOut", duration: 0.4 }}
    >
      <div className="content flex items-center justify-between py-4">
        <Link href="/" className="flex items-center gap-2">
          <Logo className="h-8 w-auto text-primary" aria-label="Slán Docs home" />
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          <div className="flex items-center gap-12">
            {navLinks?.map((link) => (
              <Button
                key={link.link || link.label}
                variant="link-secondary"
                asChild
                className={cn("font-medium text-[17px]", pathname === link.link && "text-primary")}
              >
                <Link href={link.link || "/"}>{link.label}</Link>
              </Button>
            ))}
          </div>
          {navButtons?.length > 0 && (
            <>
              <div className="h-8 w-px bg-border" aria-hidden="true" />
              <div className="flex items-center gap-3">
                {navButtons.map((btn, index) => (
                  <Button key={buttonKey(btn, index)} asChild variant={index === 0 ? "default" : "outline"} size="sm" className="text-base font-medium">
                    <Link href={btn.link || "/"}>{btn.label}</Link>
                  </Button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden relative flex items-center justify-center w-10 h-10 translate-x-2"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
        >
          <div className="flex flex-col justify-between w-5 h-3.5">
            <motion.span
              animate={mobileOpen ? { rotate: 45, y: 5.5 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="block h-0.5 w-5 bg-foreground origin-center"
            />
            <motion.span
              animate={mobileOpen ? { opacity: 0, x: -8 } : { opacity: 1, x: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="block h-0.5 w-5 bg-foreground"
            />
            <motion.span
              animate={mobileOpen ? { rotate: -45, y: -5.5 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="block h-0.5 w-5 bg-foreground origin-center"
            />
          </div>
        </button>
      </div>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-0 top-[65px] bg-background md:hidden h-[calc(100svh-65px)] overflow-hidden"
          >
            <div className="flex flex-col h-full">
              {/* Nav links */}
              <motion.div
                className="flex flex-col"
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
                  },
                }}
              >
                {navLinks?.map((link) => (
                  <motion.div
                    key={link.link || link.label}
                    variants={{
                      hidden: { opacity: 0, y: 20 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0.3, ease: "easeOut" },
                      },
                    }}
                  >
                    <Link
                      href={link.link || "/"}
                      onClick={() => setMobileOpen(false)}
                      className={cn(
                        "flex items-center px-6 py-4 text-lg font-medium border-b border-border transition-colors",
                        pathname === link.link ? "text-primary" : "text-foreground hover:text-primary"
                      )}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
              </motion.div>

              {/* Buttons at bottom */}
              {navButtons?.length > 0 && (
                <motion.div
                  className="flex flex-col gap-3 p-6"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: { opacity: 0 },
                    visible: {
                      opacity: 1,
                      transition: { staggerChildren: 0.1, delayChildren: 0.25 },
                    },
                  }}
                >
                  {navButtons.map((btn, index) => (
                    <motion.div
                      key={buttonKey(btn, index)}
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.3, ease: "easeOut" },
                        },
                      }}
                    >
                      <Button asChild variant={index === 0 ? "default" : "outline"} className="w-full">
                        <Link href={btn.link || "/"} onClick={() => setMobileOpen(false)}>
                          {btn.label}
                        </Link>
                      </Button>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
